import React, { Component } from 'react';
import { message } from 'antd';
import DashboardLayout from './DashboardLayout';
import { FlexWrapper } from './DashboardLayout.styles';
import DashboardEditor from './DashboardEditor';
import DashboardTabs from './DashboardTabs';

const getLayoutWithDeltas = (
  layout,
  viewLayouts,
  deltaVisualisations,
  deltaVisualisationDisplayType,
  deltaVisualisationUnit,
  deltaVisualisationPanelFilter,
  context,
) => {
  let viewLayoutId;

  // There are sometimes layout is undefined, had not yet find the cause.
  // This prevents app breakages if this happens again.
  try {
    viewLayoutId = layout.viewLayout;
  } catch (e) {
    message.error(e.message);
    return ({
      viewLayout: { boxLayout: [], dashboardItems: [] },
      layoutWithDeltaVis: {},
    });
  }

  const viewLayout = viewLayouts.find(l => l.pk === viewLayoutId);
  const getVisualisationState = (stateName, currProp) => (
    layout.dashboardItems.reduce((acc, curr) => ({
      ...acc,
      [curr.position]: stateName[context][curr.position] || curr[currProp],
    }), {
      ...stateName[context],
    })
  );

  const dashboardItems = getVisualisationState(deltaVisualisations, 'visualisationType');
  const dashboardItemsDisplayType = getVisualisationState(deltaVisualisationDisplayType, 'preferredVisSubType');
  const dashboardItemsUnit = getVisualisationState(deltaVisualisationUnit, 'unit');
  const dashboardItemsPanelFilter = getVisualisationState(deltaVisualisationPanelFilter, 'panelFilterApplied');

  const layoutWithDeltaVis = {
    ...layout,
    dashboardItems: Object.entries(dashboardItems)
      .map(i => ({
        position: Number(i[0]),
        visualisationType: i[1],
        preferredVisSubType: dashboardItemsDisplayType[Number(i[0])],
        unit: dashboardItemsUnit[Number(i[0])],
        panelFilterApplied: dashboardItemsPanelFilter[Number(i[0])],
      })),
  };

  return {
    viewLayout,
    layoutWithDeltaVis,
  };
};

const DASHBOARD_DEFAULT_STRUCTURE = (intialColour, viewLayout) => ({
  name: '',
  order: -1,
  isDefault: false,
  colour: intialColour,
  dashboardLayouts: [
    {
      viewLayout,
      context: 'week',
      useAsAlternate: false,
      dashboardItems: [],
    },
    {
      viewLayout,
      context: 'day',
      useAsAlternate: false,
      dashboardItems: [],
    },
  ],
  user: null,
});

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeDashboard: null, // pk or Id of active dashboard.
      editorOpen: false,
      deltaLayoutContexts: {
        week: null,
        day: null,
      },
      deltaVisualisations: {
        week: {},
        day: {},
      },
      deltaVisualisationDisplayType: {
        week: {},
        day: {},
      },
      deltaVisualisationUnit: {
        week: {},
        day: {},
      },
      deltaVisualisationPanelFilter: {
        week: {},
        day: {},
      },
      addingDashboard: false,
    };
    this.showDefaultDashboard = this.showDefaultDashboard.bind(this);
    this.updateOrAddDashboard = this.updateOrAddDashboard.bind(this);
    this.onChangeLayout = this.onChangeLayout.bind(this);
    this.onChangeGeneric = this.onChangeGeneric.bind(this);
    this.toggleEditor = this.toggleEditor.bind(this);
    this.addDashboard = this.addDashboard.bind(this);
    this.deleteDashboard = this.deleteDashboard.bind(this);
  }

  componentDidMount() {
    // First load the active dashboard.
    this.showDefaultDashboard();
  }

  // A shared change state function used by e.g. unit, display-type, panel-filter etc.
  onChangeGeneric(stateName, id, value) {
    // eslint-disable-next-line react/destructuring-assignment
    const stateData = this.state[stateName];
    const { context } = this.props;
    const newDelta = { ...stateData };
    newDelta[context][id] = value;
    return this.setState({ [stateName]: newDelta });
  }

  // Each visualisation has different units now
  onChangePrefUnit = (id, unit) => this.onChangeGeneric('deltaVisualisationUnit', id, unit);

  // Each visualisation has multiple charts and a table, choose one to be saved so that
  // it can be recalled next time.
  onChangePrefConfig = (id, displayTypeKey) => this.onChangeGeneric('deltaVisualisationDisplayType', id, displayTypeKey);

  // Some visualisation has its own filter, choose one to be saved so that
  // it can be recalled next time.
  onChangePanelFilter = (id, filterList) => this.onChangeGeneric('deltaVisualisationPanelFilter', id, filterList);

  onChangeLayout(layout) {
    const { deltaLayoutContexts } = this.state;
    const dashboard = this.rehydrateActiveDashboardLayouts([layout]);
    const rehydratedLayout = dashboard.dashboardLayouts.find(l => l.context === layout.context);
    this.setState({
      deltaLayoutContexts: {
        ...deltaLayoutContexts,
        [layout.context]: rehydratedLayout,
      },
    });
  }

  onChangeVisualisation = (id, visualisation) => {
    const { deltaVisualisations, deltaVisualisationUnit } = this.state;
    const { visualisations, context } = this.props;
    const { units = [] } = visualisations;
    const newDeltaVisualisation = { ...deltaVisualisations };
    const newDeltaUnit = { ...deltaVisualisationUnit };

    if (!visualisations[visualisation]) {
      const visualisationAlt = Object.values(visualisations)
        .find(item => item.dataEndpoint === visualisation);
      newDeltaVisualisation[context][id] = visualisationAlt ? visualisationAlt.name : null;
    } else {
      newDeltaVisualisation[context][id] = visualisation;
    }

    newDeltaUnit[context][id] = (units.length > 0) ? units[0] : 'None';

    return this.setState({
      deltaVisualisations: newDeltaVisualisation,
      deltaVisualisationUnit: newDeltaUnit,
    });
  }

  // The dashboard prop can be either a dashboard object or its pk number.
  setActiveDashboard = (activeDashboard) => {
    const { setInitialApplyFilterCalled } = this.props;
    // When tab is changed, reset initialApplyFilter and unmount old dashboard.
    setInitialApplyFilterCalled(false);

    this.setState({
      activeDashboard, // pk or id of the active dashboard.
      deltaLayoutContexts: {
        week: null,
        day: null,
      },
      deltaVisualisations: {
        week: {},
        day: {},
      },
      deltaVisualisationDisplayType: {
        week: {},
        day: {},
      },
      deltaVisualisationUnit: {
        week: {},
        day: {},
      },
      deltaVisualisationPanelFilter: {
        week: {},
        day: {},
      },
      addingDashboard: false,
    }, () => {
      // The stored filter is passed down via props. This updates
      // the parent filter state. This also updates the activeDashboar
      const { applyStoredFilter } = this.props;
      const { layout: { applyFilter = [] } } = this.getActiveDashboardAndLayout();
      applyStoredFilter(applyFilter);
    });
  };

  getActiveDashboardAndLayout() {
    const { viewLayouts, context } = this.props;
    const {
      deltaLayoutContexts, deltaVisualisations, deltaVisualisationPanelFilter,
      deltaVisualisationDisplayType, deltaVisualisationUnit,
    } = this.state;

    // Check if we're adding a new dashboard. Use a template if so.
    // If not, find the active dashboard.
    const dashboard = this.rehydrateActiveDashboardLayouts();

    // If we can't find the dashboard, render nothing.
    if (!dashboard) {
      return {
        dashboard: null,
        layout: null,
      };
    }

    // If we're editing, we want to see the changes we make reflected.
    // if the user has chosen a new layout for any of the apps contexts
    // We map it on to the dashboard passed for rendering.
    const deltaLayout = deltaLayoutContexts[context];
    const dashboardWithMappedDeltaLayouts = {
      ...dashboard,
      dashboardLayouts: dashboard.dashboardLayouts.map((l) => {
        if (deltaLayout && l.context === context) {
          return deltaLayout;
        }
        return l;
      }),
    };

    const layout = deltaLayout || dashboard.dashboardLayouts
      .find(l => l.context === context);

    const { viewLayout, layoutWithDeltaVis } = getLayoutWithDeltas(
      layout,
      viewLayouts,
      deltaVisualisations,
      deltaVisualisationDisplayType,
      deltaVisualisationUnit,
      deltaVisualisationPanelFilter,
      context,
    );

    return {
      dashboard: dashboardWithMappedDeltaLayouts,
      layout: layoutWithDeltaVis,
      viewLayout,
    };
  }

  rehydrateActiveDashboardLayouts = (layouts = []) => {
    const { activeDashboard, addingDashboard } = this.state;
    const { dashboards, viewLayouts } = this.props;
    if (!activeDashboard) return false;
    // This finds the active dashboard. If the active dashboard is deleted,
    // it conveniently shows the first one.
    const dashboard = addingDashboard
      ? DASHBOARD_DEFAULT_STRUCTURE('#0099FF', viewLayouts[0].pk)
      : (dashboards.find(d => d.pk === activeDashboard) || dashboards[0]);

    const layoutsAsObj = layouts.reduce((acc, curr) => ({
      ...acc,
      [curr.context]: curr,
    }), {});

    return {
      ...dashboard,
      dashboardLayouts: dashboard.dashboardLayouts.map(l => ({
        ...l,
        ...layoutsAsObj[l.context],
      })),
    };
  };

  showDefaultDashboard() {
    const { dashboards } = this.props;
    const activeDashboard = dashboards.find(x => x.isDefault) || dashboards[0];
    this.setActiveDashboard(activeDashboard.pk);
  }

  addDashboard() {
    this.setState({
      addingDashboard: true,
      editorOpen: true,
    });
  }

  deleteDashboard(dashboardPk) {
    const { api: { deleteDashboard } } = this.props;
    deleteDashboard(
      dashboardPk,
      () => {
        this.setState({
          editorOpen: false,
          deltaLayoutContexts: {
            week: null,
            day: null,
          },
        });
        this.showDefaultDashboard();
      },
    );
  }

  toggleEditor() {
    const { editorOpen } = this.state;
    this.setState({
      editorOpen: !editorOpen,
      deltaLayoutContexts: {
        week: null,
        day: null,
      },
    });
  }

  updateOrAddDashboard(dashboard) {
    const {
      api: { updateDashboard, addDashboard, resetDashboard },
      viewLayouts, context, globalFilterApplied, dashboards,
    } = this.props;
    const {
      deltaVisualisations, deltaVisualisationDisplayType, deltaVisualisationUnit,
      deltaVisualisationPanelFilter, addingDashboard,
    } = this.state;
    const apiCall = addingDashboard ? addDashboard : updateDashboard;
    const makeLayoutData = (useLayout, useContext) => getLayoutWithDeltas(
      useLayout,
      viewLayouts,
      deltaVisualisations,
      deltaVisualisationDisplayType,
      deltaVisualisationUnit,
      deltaVisualisationPanelFilter,
      useContext,
    );

    const mappedLayouts = this
      .rehydrateActiveDashboardLayouts(dashboard.dashboardLayouts)
      .dashboardLayouts;

    const dashboardLayouts = (dashboard.contextSpecific) ? (
      // If is context specific, map the layout accordingly
      mappedLayouts
        .map((l) => {
          const { viewLayout, layoutWithDeltaVis } = makeLayoutData(l, l.context);
          return {
            ...l,
            viewLayout: viewLayout.pk,
            dashboardItems: layoutWithDeltaVis.dashboardItems,
            applyFilter: globalFilterApplied,
          };
        })
    ) : (() => {
      // If no, only grab whatever layout is created on the current screen and apply to all.
      const appliedAllLayout = mappedLayouts.find(item => item.context === context);
      const { viewLayout, layoutWithDeltaVis } = makeLayoutData(appliedAllLayout, context);
      return mappedLayouts
        .map(({ viewLayout: viewLayoutUnused, dashboardItems: dashboardItemsUnused, ...rest }) => ({
          ...rest,
          viewLayout: viewLayout.pk,
          dashboardItems: layoutWithDeltaVis.dashboardItems,
          applyFilter: globalFilterApplied,
        }));
    })();

    const dashboardBody = { ...dashboard };
    if (addingDashboard && dashboard) {
      dashboardBody.order = Number(dashboards.sort((a, b) => b.order - a.order)[0].order + 1);
    }

    apiCall(
      dashboard.pk,
      {
        ...dashboardBody,
        dashboardLayouts,
      },
      (newSetOfDashboard) => {
        if (!addingDashboard) resetDashboard(newSetOfDashboard);
        const { activeDashboard } = this.state;
        this.setState({
          activeDashboard: addingDashboard ? newSetOfDashboard.pk : activeDashboard,
          addingDashboard: false,
          deltaLayoutContexts: {
            week: null,
            day: null,
          },
          deltaVisualisations: {
            week: {},
            day: {},
          },
          deltaVisualisationDisplayType: {
            week: {},
            day: {},
          },
          deltaVisualisationUnit: {
            week: {},
            day: {},
          },
          deltaVisualisationPanelFilter: {
            week: {},
            day: {},
          },
        });
      },
    );

    this.setState({ editorOpen: false });
  }

  render() {
    const {
      dashboards, api, visualisationData, visualisations, visualisationActions,
      viewLayouts, context, globalFilterDef, globalFilterApplied, initialApplyFilterCalled,
    } = this.props;
    const {
      activeDashboard, editorOpen,
      deltaVisualisations, // deltaVisualisation is passed down for indicating what card is active.
    } = this.state;
    const { dashboard, layout, viewLayout } = this.getActiveDashboardAndLayout();

    return (
      <>
        <FlexWrapper>
          {(activeDashboard && initialApplyFilterCalled) && (
            <>
              <DashboardEditor
                dashboard={dashboard}
                dashboards={dashboards}
                context={context}
                deleteDashboard={this.deleteDashboard}
                onChangeLayout={this.onChangeLayout}
                open={editorOpen}
                toggleEditor={this.toggleEditor}
                updateDashboard={this.updateOrAddDashboard}
                viewLayouts={viewLayouts}
              />
              <DashboardLayout
                api={api}
                editing={editorOpen}
                layout={layout}
                context={context}
                activeDashboard={activeDashboard}
                deltaVisualisations={deltaVisualisations[context]}
                onChangeVisualisation={this.onChangeVisualisation}
                onChangePrefConfig={this.onChangePrefConfig}
                onChangePrefUnit={this.onChangePrefUnit}
                onChangePanelFilter={this.onChangePanelFilter}
                toggleEditor={this.toggleEditor}
                visualisations={visualisations}
                visualisationData={visualisationData}
                visualisationActions={visualisationActions}
                viewLayout={viewLayout}
                globalFilterDef={globalFilterDef}
                globalFilterApplied={globalFilterApplied}
              />
            </>
          )}
        </FlexWrapper>
        {!editorOpen }
        <DashboardTabs
          api={api}
          dashboards={dashboards}
          activeDashboard={activeDashboard}
          setActiveDashboard={this.setActiveDashboard}
          addDashboard={this.addDashboard}
        />
      </>
    );
  }
}

export default Dashboard;
